import TAIconButton from "../../components/ta_icon_button/ta_icon_button";
import React, { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Chip, useMediaQuery } from "@mui/material";
import { DrawerHeader, StyledTopMenuSectionContainer } from "./styled";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useModalStore } from "../../store/modalStore";
import { capitalizeFirstLetter } from "../../utils/strings";

import {
  AdminPanelSettings,
  Edit,
  Menu as MenuIcon,
} from "@mui/icons-material";

import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  CssBaseline,
  Box,
  IconButton,
  Divider,
  ListItemButton,
  ListItemIcon,
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  Grid,
  Avatar,
  Stack,
} from "@mui/material";

import {
  HomeOutlined as HomeOutlinedIcon,
  PeopleAltOutlined as PeopleAltIcon,
  LogoutOutlined as LogoutOutlinedIcon,
  Close as CloseIcon,
  QueryStats,
} from "@mui/icons-material";

const DefaultLayout: React.FC = () => {
  // States
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  // Hooks
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { logout, userDetails } = useAuthStore();
  const setRoleModalOpen = useModalStore((state) => state.setRoleModalOpen);
  const setRoleEmail = useModalStore((state) => state.setRoleEmail);
  const setRole = useModalStore((state) => state.setRole);

  // Handlers
  // Toggle Drawer
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Handle Logout button click
  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  // Confirm logout action
  const confirmLogout = () => {
    toast.success("Logged out successfully!");
    logout(); // Perform logout
    setOpenLogoutDialog(false); // Close the dialog
    // Optional: Show success toast
    navigate("/login");
    window.location.reload();
  };

  // Cancel logout action
  const cancelLogout = () => {
    setOpenLogoutDialog(false); // Close the dialog without logging out
  };

  // Drawer menu items
  const menuItems = [
    {
      text: "Overview",
      icon: (
        <QueryStats
          sx={{ color: location.pathname === "/overview" ? "red" : "black" }}
        />
      ),
      path: "/overview",
    },
    {
      text: "Dashboard",
      icon: (
        <HomeOutlinedIcon
          sx={{
            color: location.pathname === "/dashboard" ? "red" : "secondary",
          }}
        />
      ),
      path: "/dashboard",
    },
  ];

  if (userDetails.role === "admin")
    menuItems.push({
      text: "Users",
      icon: (
        <PeopleAltIcon
          sx={{ color: location.pathname === "/users" ? "red" : "secondary" }}
        />
      ),
      path: "/users",
    });

  const bottomItems = [
    {
      text: "Log Out",
      textColor: "error",
      icon: <LogoutOutlinedIcon color="error" />,
      onClick: handleLogoutClick,
    },
  ];

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* AppBar */}
      <AppBar
        variant="elevation"
        position="fixed"
        sx={{
          width: { sm: drawerOpen ? "calc(100% - 260px)" : "100%" },
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          backgroundColor: "#28282B",
          boxShadow: 9,
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              boxShadow: "inset rgba(0, 0, 0, 0.1) 0px 0px 3px 0px",
            }}
          >
            <MenuIcon />
          </IconButton>

          <motion.div
            initial={{ filter: "blur(20px)", opacity: 0 }}
            animate={{ filter: "blur(0px)", opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="text-xl text-center sm:text-4xl font-bold tracking-tighter md:text-6xl md:leading-[4rem]"
          >
            <Typography
              p={1}
              fontFamily={"monospace"}
              fontWeight={600}
              variant="h3"
              sx={{ marginLeft: "auto" }} // Ensures the text aligns to the right
            >
              IGA Retail Application
            </Typography>
          </motion.div>
        </Toolbar>
      </AppBar>

      {/* Side Drawer */}
      <Drawer
        sx={{
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 240,
            boxSizing: "border-box",
            border: "none",
          },
        }}
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={drawerOpen}
        // onClose={handleDrawerToggle}
      >
        <DrawerHeader>
          <motion.image
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Box
              mr={2}
              mt={2}
              width={100}
              component={"img"}
              src="https://upload.wikimedia.org/wikipedia/commons/2/2a/IGA_logo.svg"
            />
          </motion.image>

          <TAIconButton
            variant="lg"
            icon={<CloseIcon />}
            onClick={handleDrawerToggle}
            data-testid="close-drawer"
          />
        </DrawerHeader>

        <StyledTopMenuSectionContainer>
          <List>
            {menuItems.map((item) => (
              <ListItem>
                <ListItemButton
                  component={Link}
                  to={item.path as any}
                  onClick={(
                    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
                  ) => {
                    e.preventDefault();
                    navigate(item.path as any);
                  }}
                  selected={location.pathname === item.path}
                  sx={{
                    paddingLeft: "24px",
                    paddingRight: "16px",
                    borderTopRightRadius: 10,
                    borderBottomRightRadius: 10,
                    boxShadow:
                      location.pathname === item.path
                        ? "inset rgba(0, 0, 0, 0.9) 1px 1px 1px 1px"
                        : "inset rgba(0, 0, 10, 0.05) 1px 1px 1px 1px",
                    marginRight: isMobile ? 0 : -2,
                  }}
                  disableRipple
                >
                  <ListItemText
                    primary={
                      <Typography
                        fontWeight={600}
                        color="#ewtwd"
                        textTransform={"uppercase"}
                        textAlign={"left"}
                      >
                        {item.text}
                      </Typography>
                    }
                  />
                  <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <List
            sx={{
              // backgroundColor: "#FAF9F6",
              mr: 0.3,
              ml: 2,
              mb: 1,
              borderRadius: 3,
              boxShadow: "0px 0px 2px 1px rgba(0, 0, 0, 0.1)",
            }}
          >
            {bottomItems.map((item, index) => (
              <Box>
                <Stack
                  direction="column"
                  spacing={0.5}
                  m={2}
                  alignItems="center"
                  key={index}
                >
                  <Avatar />
                  <Typography>{userDetails?.email}</Typography>
                  <Chip
                    label={capitalizeFirstLetter(userDetails?.role)}
                    icon={
                      userDetails?.role === "admin" ? (
                        <AdminPanelSettings />
                      ) : (
                        <Edit />
                      )
                    }
                  />
                  {/* TODO: To let editors make themselved admin */}
                  {/* {userDetails?.role === "editor" && (
                    <Typography
                      fontSize={12}
                      color="primary"
                      sx={{
                        cursor: "pointer",
                        ":hover": {
                          backgroundColor: "aquamarine",
                        },
                      }}
                      onClick={() => {
                        setRoleModalOpen(true);
                        setRoleEmail(userDetails?.email);
                        setRole(userDetails?.role);
                      }}
                    >
                      Become an admin?
                    </Typography>
                  )} */}
                </Stack>

                <Divider />
                <ListItem key={item.text}>
                  <Button
                    fullWidth
                    variant="text"
                    size="large"
                    sx={{
                      paddingLeft: "24px",
                      paddingRight: "16px",
                      borderRadius: 2,
                      borderColor: "black",
                      ":hover": {
                        border: "1px solid black",
                        borderColor: "black",
                      },
                    }}
                    onClick={item.onClick}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="black"
                      mr={2}
                    >
                      {item.text}
                    </Typography>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {item.icon}
                    </ListItemIcon>
                  </Button>
                </ListItem>
              </Box>
            ))}
          </List>
        </StyledTopMenuSectionContainer>
        <Divider />
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          paddingTop: 8,
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginLeft: { sm: drawerOpen ? "260px" : "0" },
          transitionDuration: "0.3s",
          backgroundColor: "#EEF2F7",
          // height: "100%",
        }}
      >
        <Box>
          <Outlet />
        </Box>
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={openLogoutDialog}
        onClose={cancelLogout}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "0.938rem",
            width: "28.125rem",
            height: "auto",
          },
        }}
      >
        <Grid
          container
          display={"flex"}
          justifyContent={"space-between"}
          p={1}
          sx={{
            backgroundColor: "#343434",
            color: "white",
          }}
        >
          <Typography variant="h6">{"Confirm Logout"}</Typography>
          <Grid
            item
            onClick={() => {
              setOpenLogoutDialog(false);
            }}
            mt={0.2}
            sx={{
              cursor: "pointer",
            }}
          >
            <CloseIcon />
          </Grid>
        </Grid>

        <DialogContent>
          <Typography>Are you sure you want to log out?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelLogout} color="error" variant="outlined">
            Cancel
          </Button>
          <Button onClick={confirmLogout} color="error" variant="contained">
            Log Out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DefaultLayout;
